'use client';

import { usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, ReactNode } from 'react';
import { extractLocaleFromPathname } from '@/app/_lib/locale-utils';
import { consentManagerAdapter } from '@custom/consent-manager';

const LocaleContext = createContext<string>('de');

/**
 * Derives the locale from the URL pathname and keeps <html lang> in sync.
 * Falls back to defaultLocale for non-prefixed paths (e.g. /about -> 'de').
 */
export function LocaleProvider({
  children,
  defaultLocale = 'de',
}: {
  children: ReactNode;
  defaultLocale?: string;
}) {
  const pathname = usePathname();
  const locale = extractLocaleFromPathname(pathname, defaultLocale);

  // Keep <html lang> and consent banner language in sync after client-side navigation
  useEffect(() => {
    document.documentElement.lang = locale;
    consentManagerAdapter.updateLanguage?.(locale);
  }, [locale]);

  return (
    <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): string {
  return useContext(LocaleContext);
}
